import { FieldEditorData, FieldEditorLabelPosition, FieldEditorType } from './FieldEditorProps';

/**
 * Converts an array of strings to a datasource array.
 * 
 * @param stringArray 
 */
export function stringArrayToDataSource(stringArray: string[])
{
    let dataSource: any[] = [];

    for (let str of stringArray)
    {
        let dataSourceRow = {
            value: str,
            display: str
        };
        dataSource.push(dataSourceRow);
    }

    return dataSource;
}

/**
 * Converts an array of numbers to a datasource array.
 */
export function numberArrayToDataSource(numberArray: number[])
{
    let dataSource: any[] = [];

    for (let num of numberArray)
    {
        let dataSourceRow = {
            value: num,
            display: num
        };
        dataSource.push(dataSourceRow);
    }

    return dataSource;
}

/**
 * Converts an enum to a datasource array. 
 * When using with a FieldEditor component, you will have to use value.value to get the real value. Eg: updateEntityField(field, value?.value)}
 * 
 * @param enumObject The object that needs to be converted.
 * @param t A function to translate the key for display.
 * @param translationPrefix The prefix for the translation key. Eg: 'ORDER_STATUS_' + key
 */
export function enumToDataSource(enumObject: any, t?: (text: string) => string, translationPrefix?: string)
{
    let dataSource: any[] = [];

    for (let [key, value] of Object.entries(enumObject))
    {
        let dataSourceRow = {
            value: value,
            display: t ? t(translationPrefix ? translationPrefix + key : key) : key
        };
        dataSource.push(dataSourceRow);
    }

    return dataSource;
}

/**
 * Get the value for the dataSourceDisplayMember.
 * 
 * @param item The datasource item (row).
 * @param dataSourceDisplayMember The display member.
 * @returns 
 */
export function getDataSourceDisplayMemberValue(item: any, dataSourceDisplayMember: string)
{
    let displayValue: any;

    if (!dataSourceDisplayMember.includes('.'))
    {// Simple member
        displayValue = item[dataSourceDisplayMember];
    }
    else
    {// Complex member. Eg: actor.person.lastName
        let memberObject = item;

        let fieldSplits = dataSourceDisplayMember.split('.');
        for (let fieldSplit of fieldSplits)
        {
            memberObject = memberObject[fieldSplit];
        }

        if (memberObject !== undefined) displayValue = memberObject;
    }

    return displayValue;
}

/**
 * Find all the field editor input HTML elements in a div.
 */
export function findFieldEditorInputs(div: HTMLDivElement): HTMLElement[]
{
    let inputs: HTMLElement[] = [];

    let elements = div.querySelectorAll('.field-editor-input');
    elements.forEach(element =>
    {
        inputs.push(element as HTMLElement);
    });

    return inputs;
}

/**
 * Check the validity of the field editor input controls behind the FieldEditor component.
 * Mark the inputs as invalid or valid with the 'is-invalid' class.
 * 
 * @param inputs The input elements to check.
 * @returns True if all the inputs are valid, false otherwise.
 */
export function checkValidityOfFieldEditorInputs(inputs: HTMLElement[]): { isValid: boolean, invalidFieldEditorInfos: FieldEditorData[] }
{
    let isValid = true;
    let invalidFieldEditorInfos: FieldEditorData[] = [];

    let countInvalid = 0;
    for (let input of inputs)
    {
        if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement)
        {
            if (!input.checkValidity())
            {
                countInvalid++;
                input.classList.add('is-invalid');
                let fieldEditorData = getFieldEditorData(input);
                invalidFieldEditorInfos.push(fieldEditorData);
            }
            else
            {
                input.classList.remove('is-invalid');
            }
        }
    }

    isValid = countInvalid === 0;

    return {
        isValid,
        invalidFieldEditorInfos
    };
}

/**
 * Reset the field editor inputs. Remove the 'is-invalid' class from all the inputs.
 * 
 * @param inputs The input elements to reset.
 */
export function resetFieldEditorInputs(inputs: HTMLElement[])
{
    for (let input of inputs)
    {
        input.classList.remove('is-invalid');
    }
}

/**
 * Extract the field editor data for the input element inside a FieldEditor component.
 * 
 * @param input The input element.
 * @returns The field editor data.
 */
export function getFieldEditorData(input: HTMLElement): FieldEditorData
{
    // get the closes parent of the input that has class 'field-editor'
    let fieldEditor = input.closest('.field-editor') as HTMLElement;

    return {
        type: fieldEditor.getAttribute('data-field-editor-type') as FieldEditorType,
        field: fieldEditor.getAttribute('data-field-editor-field') as string,
        label: fieldEditor.getAttribute('data-field-editor-label') as string,
        labelPosition: fieldEditor.getAttribute('data-field-editor-label-position') != null ? parseInt(fieldEditor.getAttribute('data-field-editor-label-position')!) as FieldEditorLabelPosition : undefined,
        placeholder: fieldEditor.getAttribute('data-field-editor-placeholder') as string,
        isRequired: fieldEditor.getAttribute('data-field-editor-is-required') === 'true',
        isVisible: fieldEditor.getAttribute('data-field-editor-is-visible') === 'true',
        isEnabled: fieldEditor.getAttribute('data-field-editor-is-enabled') === 'true',
    };
}